import {
  DEVELOPMENT_ACCUMULATOR_DOMAINS,
  GROUP_CONFIGS,
  MATERIALS_ACCUMULATOR_DOMAINS,
} from '../../../config/globalSearchConsts'

export const getBoldText = (text: string, searchTerm: string) => {
  if (!searchTerm) return text
  const index = text.toLowerCase().indexOf(searchTerm.toLowerCase())
  if (index === -1) return text
  const before = text.slice(0, index)
  const match = text.slice(index, index + searchTerm.length)
  const after = text.slice(index + searchTerm.length)
  return (
    <>
      {before}
      <strong>{match}</strong>
      {after}
    </>
  )
}

export const setInputFocus = (focus: boolean) => {
  const inputElement = document.getElementById('quick-search-input')
  if (focus && inputElement) {
    inputElement.focus()
  }
}

export const getDomainLabel = (domain: string) => {
  switch (domain) {
    case 'DESIGN':
      return 'Design'
    case 'Product Development':
      return 'Development'
    case 'MATERIALS':
      return 'Materials'
    case 'COMBINATIONS':
      return 'Combinations'
    case 'PALETTES_LOAD':
      return 'Palettes'
    case 'COSTING':
      return 'Negotiation events'
    default:
      return domain
  }
}

export const preventToggleWhenOpen =
  (isOpen: boolean) => (event: React.MouseEvent) => {
    if (isOpen) {
      event.stopPropagation()
    }
  }

export const selectItemByIndex = (
  selectedIndex: number,
  recentSearchesData: { searchTerm: string }[],
  flattenedDomainItems: any[],
  handleSelectRecentSearch: (searchTerm: string) => void,
  selectDomainSearchResult: (item: any) => void,
) => {
  if (selectedIndex < 0) {
    return
  }

  if (selectedIndex < recentSearchesData.length) {
    handleSelectRecentSearch(recentSearchesData[selectedIndex].searchTerm)
    return
  }

  const domainItemIndex = selectedIndex - recentSearchesData.length
  const selectedDomainItem = flattenedDomainItems[domainItemIndex]
  if (selectedDomainItem) {
    selectDomainSearchResult(selectedDomainItem)
  }
}

export const handleKeyDown = (
  e: React.KeyboardEvent,
  {
    totalSelectableItems,
    setShowQuickSearchDropdown,
    setIndex,
    recentSearchesData,
    setSearchTerm,
    index,
    searchTerm,
    flattenedDomainItems,
    handleSelectRecentSearch,
    selectDomainSearchResult,
  }: {
    totalSelectableItems: number
    setShowQuickSearchDropdown: (show: boolean) => void
    setIndex: React.Dispatch<React.SetStateAction<number>>
    recentSearchesData: { searchTerm: string }[]
    setSearchTerm: (term: string) => void
    index: number
    searchTerm: string
    flattenedDomainItems: any[]
    handleSelectRecentSearch: (searchTerm: string) => void
    selectDomainSearchResult: (item: any) => void
  },
) => {
  if (e.key === 'ArrowDown' && totalSelectableItems > 0) {
    e.preventDefault()
    setShowQuickSearchDropdown(true)
    setIndex((prev) => {
      const nextIndex =
        prev < 0 ? 0 : Math.min(prev + 1, totalSelectableItems - 1)

      if (nextIndex < recentSearchesData.length) {
        setSearchTerm(recentSearchesData[nextIndex].searchTerm)
      }

      return nextIndex
    })
  }

  if (e.key === 'ArrowUp' && totalSelectableItems > 0) {
    e.preventDefault()
    setShowQuickSearchDropdown(true)
    setIndex((prev) => {
      const nextIndex = prev <= 0 ? 0 : prev - 1
      if (nextIndex < recentSearchesData.length) {
        setSearchTerm(recentSearchesData[nextIndex].searchTerm)
      }
      return nextIndex
    })
  }

  if (e.key === 'Enter' && index >= 0) {
    e.preventDefault()
    selectItemByIndex(
      index,
      recentSearchesData,
      flattenedDomainItems,
      handleSelectRecentSearch,
      selectDomainSearchResult,
    )
    return
  }

  if (e.key === 'Enter' && index < 0 && searchTerm !== '') {
    handleSelectRecentSearch(searchTerm)
  }
}

export const getResourceLink = (id: string, domain: string) => {
  const type = (domain || '').toLowerCase()

  switch (type) {
    case 'products':
      return { url: `/products/${id}`, requiresFullNavigation: false }
    case 'workspace':
      return {
        url: `/workspaces/${id}/products`,
        requiresFullNavigation: false,
      }
    case 'product_template':
      return {
        url: `/templateLibrary/productTemplates/${id}`,
        requiresFullNavigation: false,
      }
    case 'combination':
      return { url: `/combinations/${id}`, requiresFullNavigation: false }
    case 'palette':
      return { url: `/palettes/${id}`, requiresFullNavigation: false }
    case 'team':
      return { url: `/teams/${id}`, requiresFullNavigation: false }
    case 'material':
      return {
        url: `/resourceRedirect/${id}`,
        requiresFullNavigation: true,
      }
    default:
      return {
        url: `/resourceRedirect/${id}`,
        requiresFullNavigation: true,
      }
  }
}

export const getRefineSearchLink = (domain: string, searchTerm: string) => {
  switch (domain) {
    case 'PRODUCT_DEVELOPMENT':
      return `/products?q=${searchTerm}`
    case 'Product Development':
      return `/products?q=${searchTerm}`
    case 'MATERIALS':
      return `/materials?q=${searchTerm}`
    case 'COMBINATIONS':
      return `/combinations?q=${searchTerm}`
    case 'PALETTES_LOAD':
      return `/palettes?q=${searchTerm}`
    default:
      return ''
  }
}

const getGroupConfigDomain = (domain: string) => {
  const normalizedDomain = domain.toUpperCase()

  if (
    DEVELOPMENT_ACCUMULATOR_DOMAINS.some(
      (accumulatorDomain) =>
        accumulatorDomain.toUpperCase() === normalizedDomain,
    )
  ) {
    return 'PRODUCT_DEVELOPMENT'
  }

  if (
    MATERIALS_ACCUMULATOR_DOMAINS.some(
      (accumulatorDomain) =>
        accumulatorDomain.toUpperCase() === normalizedDomain,
    )
  ) {
    return 'MATERIALS'
  }

  return domain
}

export const getDomainIcon = (domain: string) => {
  const mappedDomain = getGroupConfigDomain(domain)

  return (
    GROUP_CONFIGS.find((group) => group.domain === mappedDomain)?.icon || null
  )
}

export const getDomainIconColor = (domain: string) => {
  const mappedDomain = getGroupConfigDomain(domain)

  return (
    GROUP_CONFIGS.find((group) => group.domain === mappedDomain)?.iconColor ||
    ''
  )
}
